import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Image as ImageIcon, Video, Coins, Info } from 'lucide-react';
import { 
  getSchoolStudentWallets, 
  getSchoolTokenLogs,
  type StudentWallet,
  type TokenUsageLog 
} from '../../lib/apis_vibe';
import { getSchools, getRoles, getMemos, type School } from '../../lib/api';

import TokenHeader from './components/TokenHeader';
import TokenTabs from './components/TokenTabs';
import WalletListTab from './components/WalletListTab';
import SchoolLogsTab from './components/SchoolLogsTab';
import RechargeModal from './components/RechargeModal';
import LogViewer from './components/LogViewer';

const TokenomicsDashboard = () => (
  <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
    <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-5 text-white shadow-lg shadow-indigo-200 relative overflow-hidden flex flex-col justify-center">
      <div className="absolute -right-4 -top-4 opacity-10">
        <Coins size={100} />
      </div>
      <h3 className="text-indigo-100 text-sm font-medium mb-1 flex items-center gap-1">
        <Info size={14} /> 平台基准汇率
      </h3>
      <div className="text-3xl font-bold tracking-tight">1.00 <span className="text-lg font-normal text-indigo-200">CNY</span></div>
      <div className="text-sm font-medium mt-1 text-indigo-100">
        ≈ <span className="text-white font-bold text-lg">80,000</span> 算力点
      </div>
    </div>

    <div className="bg-white rounded-2xl p-5 border border-indigo-50 shadow-sm hover:shadow-md transition-shadow relative group">
      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
        <Zap size={20} />
      </div>
      <h4 className="text-gray-500 text-sm font-medium">📝 文本对话能力</h4>
      <div className="text-xl font-bold text-gray-800 mt-1">约 20,000 字</div>
      <p className="text-xs text-gray-400 mt-2">消耗基准: 1点/输入，6点/输出</p>
    </div>

    <div className="bg-white rounded-2xl p-5 border border-indigo-50 shadow-sm hover:shadow-md transition-shadow relative group">
      <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
        <ImageIcon size={20} />
      </div>
      <h4 className="text-gray-500 text-sm font-medium">🎨 图像生成能力</h4>
      <div className="text-xl font-bold text-gray-800 mt-1">约 1.7 张</div>
      <p className="text-xs text-gray-400 mt-2">单张平均消耗: 45,000 点</p>
    </div>

    <div className="bg-white rounded-2xl p-5 border border-indigo-50 shadow-sm hover:shadow-md transition-shadow relative group">
      <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
        <Video size={20} />
      </div>
      <h4 className="text-gray-500 text-sm font-medium">🎬 视频锻造能力</h4>
      <div className="text-xl font-bold text-gray-800 mt-1">需 7.5 元 / 个</div>
      <p className="text-xs text-gray-400 mt-2">单次一口价消耗: 600,000 点</p>
    </div>
  </div>
);

const TokenManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'wallets' | 'logs'>('wallets');

  const [students, setStudents] = useState<StudentWallet[]>([]);
  const [schoolLogs, setSchoolLogs] = useState<TokenUsageLog[]>([]);
  const [loading, setLoading] = useState(false);

  const [roles, setRoles] = useState<string[]>([]);
  const [memos, setMemos] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedMemo, setSelectedMemo] = useState<string>('');

  const [rechargeModal, setRechargeModal] = useState<{
    isOpen: boolean;
    target: { type: 'single'; user: StudentWallet } | { type: 'batch'; schoolId: number; schoolName: string } | null;
    mode: 'recharge' | 'deduct';
  }>({ isOpen: false, target: null, mode: 'recharge' });

  const [logViewer, setLogViewer] = useState<{
    isOpen: boolean;
    student: StudentWallet | null;
  }>({ isOpen: false, student: null });

  useEffect(() => {
    getSchools({ limit: 200 }).then(res => {
      const list = Array.isArray(res) ? res : (res as any).data || [];
      setSchools(list);
      if (list.length > 0) setSelectedSchoolId(list[0].id);
    }).catch(console.error);

    getRoles().then(res => {
      const data = (res as any).data || res;
      setRoles(Array.isArray(data) ? data : []);
    }).catch(console.error);

    getMemos().then(res => {
      const data = (res as any).data || res;
      setMemos(Array.isArray(data) ? data : []);
    }).catch(console.error);
  }, []);

  const loadData = async () => {
    if (!selectedSchoolId) return;
    setLoading(true);
    try {
      if (activeTab === 'wallets') {
        const res = await getSchoolStudentWallets(selectedSchoolId, {
          skip: 0,
          limit: 2000,
          role: selectedRole || undefined,
          memo: selectedMemo || undefined
        });
        setStudents((res as any).data || res);
      } else {
        const res = await getSchoolTokenLogs(selectedSchoolId, 0, 2000);
        setSchoolLogs((res as any).data || res);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedSchoolId, activeTab, selectedRole, selectedMemo]);

  const currentSchoolName = schools.find(s => s.id === selectedSchoolId)?.schoolname || 
                            (schools.find(s => s.id === selectedSchoolId) as any)?.name || '未知机构';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-6 font-sans relative">
      
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        <div className="bg-white/60 backdrop-blur-md border border-white/80 shadow-sm rounded-2xl mb-6 overflow-hidden">
          <TokenHeader 
            schools={schools}
            selectedSchoolId={selectedSchoolId}
            onSchoolChange={setSelectedSchoolId}
            onBack={() => navigate('/admin')}
          />
        </div>

        <TokenomicsDashboard />

        <div className="bg-white/80 backdrop-blur-xl border border-white shadow-xl shadow-indigo-100/20 rounded-3xl overflow-hidden flex flex-col min-h-[600px]">
          
          <div className="bg-gray-50/50 border-b border-gray-100 px-6 pt-4">
            <TokenTabs 
              activeTab={activeTab} 
              onTabChange={setActiveTab} 
            />
          </div>

          <div className="p-6 flex-1 bg-white/50">
            {activeTab === 'wallets' ? (
              <WalletListTab 
                students={students}
                loading={loading}
                currentSchoolName={currentSchoolName}
                onBatchRecharge={() => setRechargeModal({ 
                  isOpen: true, 
                  target: { type: 'batch', schoolId: selectedSchoolId, schoolName: currentSchoolName },
                  mode: 'recharge'
                })}
                onSingleRecharge={(user) => setRechargeModal({ 
                  isOpen: true, 
                  target: { type: 'single', user },
                  mode: 'recharge'
                })}
                onSingleDeduct={(user) => setRechargeModal({ 
                  isOpen: true, 
                  target: { type: 'single', user },
                  mode: 'deduct'
                })}
                onViewLogs={(user) => setLogViewer({ isOpen: true, student: user })}
                roles={roles}
                memos={memos}
                selectedRole={selectedRole}
                selectedMemo={selectedMemo}
                onRoleChange={setSelectedRole}
                onMemoChange={setSelectedMemo}
              />
            ) : (
              <SchoolLogsTab 
                logs={schoolLogs}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {rechargeModal.target && (
        <RechargeModal 
          isOpen={rechargeModal.isOpen} 
          target={rechargeModal.target} 
          mode={rechargeModal.mode}
          onClose={() => setRechargeModal({ ...rechargeModal, isOpen: false })}
          onSuccess={loadData}
        />
      )}

      {logViewer.student && (
        <LogViewer 
          isOpen={logViewer.isOpen}
          student={logViewer.student}
          onClose={() => setLogViewer({ ...logViewer, isOpen: false })}
        />
      )}
    </div>
  );
};

export default TokenManagementPage;
